import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useState } from 'react'
import { IPatient, IGetPatientParams } from '~/interfaces'
import { useFetchPatient, useMaternalInfantViewSettings } from '~/hooks'

interface PatientProviderProps {
  children: ReactNode
}

const defaultValue = {
  patient: {} as IPatient,
  isLoading: false,
  errorPatient: false,
  openPatientModal: false
}

export interface PatientContextHelpers {
  isLoading: boolean
  errorPatient: string | boolean
  openPatientModal: boolean
  setOpenPatientModal: Dispatch<SetStateAction<boolean>>
}

export interface PatientContextProps extends PatientContextHelpers {
  patient: IPatient
  getPatient: (params: IGetPatientParams) => void
  getVisiblePatientId: () => number | undefined
  resetPatient: VoidFunction

  patientTag: string | null
  resetPatientTag: VoidFunction
  setNewPatientTag: (tagText: string) => void
}

const PatientContext = createContext<PatientContextProps>({ ...defaultValue } as PatientContextProps)

const PatientProvider: React.FC<PatientProviderProps> = ({ children }: PatientProviderProps) => {
  const { getMaternalInfantViewSettings } = useMaternalInfantViewSettings()
  const [openPatientModal, setOpenPatientModal] = useState(defaultValue.openPatientModal)
  const [patientParams, setPatientParams] = useState<IGetPatientParams | null>(null)
  const { data: patient, isFetching: isLoading, error: errorPatient } = useFetchPatient(patientParams)
  const [patientTag, setPatientTag] = useState<string | null>(null)

  const getPatient = useCallback((params: IGetPatientParams): void => {
    setPatientParams(params)
  }, [])

  const resetPatient = () => {
    setPatientParams(null)
  }

  const resetPatientTag = useCallback((): void => {
    setPatientTag(null)
  }, [])

  const setNewPatientTag = (tagText: string): void => {
    setPatientTag(tagText)
  }

  const getVisiblePatientId = useCallback(() => {
    const { selectedTimeline, selectedBaby } = getMaternalInfantViewSettings()

    if (selectedTimeline === 'baby' && selectedBaby) {
      return selectedBaby
    }
    return patient?.id
  }, [getMaternalInfantViewSettings, patient?.id])

  return (
    <PatientContext.Provider
      value={{
        patient: patient as IPatient,
        getPatient,
        patientTag,
        resetPatientTag,
        setNewPatientTag,
        isLoading,
        openPatientModal,
        setOpenPatientModal,
        resetPatient,
        getVisiblePatientId,

        errorPatient: errorPatient?.message ?? false
      }}
    >
      {children}
    </PatientContext.Provider>
  )
}

const usePatient = (): PatientContextProps => {
  return React.useContext(PatientContext)
}

export { PatientContext, PatientProvider, usePatient }
